import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, X } from 'lucide-react';

// Pre-defined agenda items for "Regular Meeting and Observance" meeting type
const REGULAR_MEETING_AGENDA_ITEMS = [
  { title: "Prayer of Invocation", notes: "Bespoke to your association" },
  { title: "Spiritual study or reading", notes: "Sharing out loud an excerpt from a spiritual text" },
  { title: "Roll call of Officers and Members", notes: "" },
  { title: "New Member recognition", notes: "" },
  { title: "Assessment of stewardship in past period", notes: "How has stewardship impacted your decisions and actions regarding; finances, family matters, health and wellbeing, spiritual practice?" },
  { title: "Financial disclosures and reporting", notes: "Share any important financial matters of the association" },
  { title: "Review minutes and open items", notes: "" },
  { title: "Open Discussion", notes: "" },
  { title: "New Actions or Resolutions", notes: "Discuss resolutions to vote on (these will be voted on later)" },
  { title: "Nominations, Elections and Installation of Officers", notes: "" },
  { title: "Closing Remarks by Presiding Authority", notes: "" },
  { title: "Re-affirmation of personal attestation", notes: "" },
  { title: "Prayer of Benediction", notes: "Bespoke to your association" }
];

// Helper to format member_role enum to display text
const formatMemberRole = (role: string | null | undefined): string => {
  if (!role) return 'Member';
  const roleMap: Record<string, string> = {
    'treasury_secretary': 'Secretary Treasurer',
    'managing_member': 'Managing Member',
    'board_member_active': 'Board Member',
    'board_member_passive': 'Board Member',
    'operations_manager': 'Operations Manager',
    'advisor': 'Advisor',
    'observer': 'Observer',
    'member': 'Member',
  };
  return roleMap[role.toLowerCase()] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

interface ExternalGuest {
  guest_name: string;
  guest_role: string;
  guest_organization: string;
  guest_email?: string;
}

interface ConductingMeetingDataFieldsProps {
  workflowData: any;
  setWorkflowData: (data: any) => void;
  unaId: string;
}

export const ConductingMeetingDataFields: React.FC<ConductingMeetingDataFieldsProps> = ({
  workflowData,
  setWorkflowData,
  unaId,
}) => {
  // Fetch UNA members for attendee selection
  const { data: unaMembers } = useQuery({
    queryKey: ['una-members', unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('una_members')
        .select('*')
        .eq('una_id', unaId)
        .eq('status', 'active')
        .order('member_role');
      
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch Secretary Treasurer from unas table and user profile address for prepopulation
  const { data: unaData } = useQuery({
    queryKey: ['una-treasury-secretary', unaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('unas')
        .select('treasury_secretary_id, primary_officer_role')
        .eq('id', unaId)
        .maybeSingle();
      
      if (error) throw error;
      
      const primaryOfficerRole = data?.primary_officer_role || 'Secretary Treasurer';
      
      // Fetch profile separately if treasury_secretary_id exists (including address)
      if (data?.treasury_secretary_id) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, full_name, email, address, city, state, zip, country')
          .eq('id', data.treasury_secretary_id)
          .maybeSingle();
        
        return {
          treasury_secretary_id: data.treasury_secretary_id,
          primary_officer_role: primaryOfficerRole,
          profile: profileData as { id: string; full_name: string; email: string; address: string | null; city: string | null; state: string | null; zip: string | null; country: string | null } | null
        };
      }
      
      return { treasury_secretary_id: null, primary_officer_role: primaryOfficerRole, profile: null };
    },
  });

  // Prepopulate meeting_location from profile address if not already set
  useEffect(() => {
    if (unaData?.profile && !workflowData.meeting_location) {
      const addressParts = [
        unaData.profile.address,
        [unaData.profile.city, unaData.profile.state, unaData.profile.zip].filter(Boolean).join(', ')
      ].filter(Boolean);
      if (unaData.profile.country && unaData.profile.country !== 'US') {
        addressParts.push(unaData.profile.country);
      }
      const fullAddress = addressParts.join(', ');
      
      if (fullAddress) {
        setWorkflowData({ ...workflowData, meeting_location: fullAddress });
      }
    }
  }, [unaData]);

  // Combine Primary Officer with eligible meeting conductors
  const meetingConductors = useMemo(() => {
    const conductors: Array<{ id: string; name: string; role: string; isTreasurer: boolean }> = [];
    const primaryOfficerRole = unaData?.primary_officer_role || 'Secretary Treasurer';
    
    // Add Primary Officer first if available
    if (unaData?.treasury_secretary_id && unaData?.profile) {
      conductors.push({
        id: unaData.treasury_secretary_id,
        name: unaData.profile.full_name || primaryOfficerRole,
        role: primaryOfficerRole,
        isTreasurer: true,
      });
    }
    
    // Add eligible members (managing members, active board members, operations managers)
    const eligibleRoles = ['managing_member', 'board_member_active', 'operations_manager'];
    unaMembers?.forEach(member => {
      const memberRole = member.member_role?.toLowerCase();
      // Skip if already added as Secretary Treasurer
      if (unaData?.treasury_secretary_id && member.user_id === unaData.treasury_secretary_id) {
        return;
      }
      if (memberRole && eligibleRoles.includes(memberRole)) {
        conductors.push({
          id: member.id,
          name: member.full_name,
          role: formatMemberRole(member.member_role),
          isTreasurer: false,
        });
      }
    });
    
    return conductors;
  }, [unaMembers, unaData]);

  // Combine Primary Officer with all active members for recording officer (anyone can take minutes)
  const recordingOfficers = useMemo(() => {
    const officers: Array<{ id: string; name: string; role: string; isTreasurer: boolean }> = [];
    const primaryOfficerRole = unaData?.primary_officer_role || 'Secretary Treasurer';
    
    // Add Primary Officer first if available
    if (unaData?.treasury_secretary_id && unaData?.profile) {
      officers.push({
        id: unaData.treasury_secretary_id,
        name: unaData.profile.full_name || primaryOfficerRole,
        role: primaryOfficerRole,
        isTreasurer: true,
      });
    }
    
    // Add all active UNA members (anyone can take minutes)
    unaMembers?.forEach(member => {
      // Skip if already added as Secretary Treasurer
      if (unaData?.treasury_secretary_id && member.user_id === unaData.treasury_secretary_id) {
        return;
      }
      officers.push({
        id: member.id,
        name: member.full_name,
        role: formatMemberRole(member.member_role),
        isTreasurer: false,
      });
    });
    
    return officers;
  }, [unaMembers, unaData]);

  // Combine Primary Officer with all active members for attendee selection
  const allAttendees = useMemo(() => {
    const attendees: Array<{ id: string; full_name: string; role: string; member_role?: string }> = [];
    const primaryOfficerRole = unaData?.primary_officer_role || 'Secretary Treasurer';
    
    // Add Primary Officer first if available
    if (unaData?.treasury_secretary_id && unaData?.profile) {
      attendees.push({
        id: unaData.treasury_secretary_id,
        full_name: unaData.profile.full_name || primaryOfficerRole,
        role: primaryOfficerRole,
        member_role: 'treasury_secretary',
      });
    }
    
    // Add all active UNA members
    unaMembers?.forEach(member => {
      // Skip if already added as Secretary Treasurer
      if (unaData?.treasury_secretary_id && member.user_id === unaData.treasury_secretary_id) {
        return;
      }
      attendees.push({
        id: member.id,
        full_name: member.full_name,
        role: formatMemberRole(member.member_role),
        member_role: member.member_role,
      });
    });
    
    return attendees;
  }, [unaMembers, unaData]);

  // Initialize with defaults ONLY if no pre-populated data exists
  useEffect(() => {
    // Check if we have pre-populated data (from initialWorkflowData in special meeting context)
    const hasPrePopulatedData = workflowData.meeting_type || workflowData.meeting_conductor_id;
    
    if (!workflowData.meeting_date && !hasPrePopulatedData) {
      // No pre-populated data - use standard defaults (7 days from now)
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      
      setWorkflowData({
        ...workflowData,
        meeting_date: sevenDaysFromNow.toISOString().split('T')[0],
        meeting_time: '14:00',
        meeting_format: 'In-Person',
        required_attendees: [],
        optional_attendees: [],
        external_guests: [],
      });
    } else if (!workflowData.required_attendees) {
      // Has some pre-populated data but missing arrays - just initialize the arrays
      setWorkflowData({
        ...workflowData,
        required_attendees: workflowData.required_attendees || [],
        optional_attendees: workflowData.optional_attendees || [],
        external_guests: workflowData.external_guests || [],
      });
    }
  }, []);

  // External guests state
  const [externalGuests, setExternalGuests] = useState<ExternalGuest[]>(
    workflowData.external_guests || []
  );

  // Update parent state when external guests change
  useEffect(() => {
    if (externalGuests.length > 0) {
      setWorkflowData({
        ...workflowData,
        external_guests: externalGuests,
      });
    }
  }, [externalGuests]);

  // Pre-populate agenda items when "Regular Meeting and Observance" is selected
  useEffect(() => {
    if (workflowData.meeting_type === 'Regular Meeting and Observance') {
      // Only pre-populate if agenda_items is empty or doesn't exist
      if (!workflowData.agenda_items || workflowData.agenda_items.length === 0) {
        const prePopulatedAgenda = REGULAR_MEETING_AGENDA_ITEMS.map((item, index) => ({
          title: item.title,
          description: item.notes,
          presenter: '',
          time_allocated: '',
          order: index + 1,
          completed: false,
          discussion_notes: '',
          is_suggested: true,
          is_required: false
        }));
        
        setWorkflowData({
          ...workflowData,
          agenda_items: prePopulatedAgenda
        });
      }
    }
  }, [workflowData.meeting_type]);

  const addExternalGuest = () => {
    setExternalGuests([
      ...externalGuests,
      { guest_name: '', guest_role: '', guest_organization: '', guest_email: '' },
    ]);
  };

  const removeExternalGuest = (index: number) => {
    setExternalGuests(externalGuests.filter((_, i) => i !== index));
  };

  const updateExternalGuest = (index: number, field: keyof ExternalGuest, value: string) => {
    const updated = [...externalGuests];
    updated[index] = { ...updated[index], [field]: value };
    setExternalGuests(updated);
  };

  const toggleRequiredAttendee = (member: any) => {
    const current = workflowData.required_attendees || [];
    const exists = current.find((a: any) => a.id === member.id);
    
    if (exists) {
      setWorkflowData({
        ...workflowData,
        required_attendees: current.filter((a: any) => a.id !== member.id),
      });
    } else {
      setWorkflowData({
        ...workflowData,
        required_attendees: [
          ...current,
          { id: member.id, name: member.full_name, role: member.role },
        ],
      });
    }
  };

  const toggleOptionalAttendee = (member: any) => {
    const current = workflowData.optional_attendees || [];
    const exists = current.find((a: any) => a.id === member.id);
    
    if (exists) {
      setWorkflowData({
        ...workflowData,
        optional_attendees: current.filter((a: any) => a.id !== member.id),
      });
    } else {
      setWorkflowData({
        ...workflowData,
        optional_attendees: [
          ...current,
          { id: member.id, name: member.full_name, role: member.role },
        ],
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Meeting Information Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Meeting Information</h3>
        
        <div className="space-y-2">
          <Label htmlFor="meeting_type">Meeting Type *</Label>
          <Select
            value={workflowData.meeting_type}
            onValueChange={(value) =>
              setWorkflowData({ ...workflowData, meeting_type: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select meeting type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Regular Meeting and Observance">Regular Meeting and Observance</SelectItem>
              <SelectItem value="Board Meeting">Board Meeting</SelectItem>
              <SelectItem value="Member Meeting">Member Meeting</SelectItem>
              <SelectItem value="Committee Meeting">Committee Meeting</SelectItem>
              <SelectItem value="Recurring Meeting">Recurring Meeting</SelectItem>
              <SelectItem value="Special Meeting">Special Meeting</SelectItem>
              <SelectItem value="Strategy Session">Strategy Session</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meeting_purpose">Meeting Purpose *</Label>
          <Textarea
            id="meeting_purpose"
            value={workflowData.meeting_purpose || ''}
            onChange={(e) =>
              setWorkflowData({ ...workflowData, meeting_purpose: e.target.value })
            }
            placeholder="Provide a brief description of why this meeting is being held"
            rows={3}
          />
          <p className="text-sm text-muted-foreground">
            e.g., "Quarterly board review and approval of asset transfers"
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="meeting_date">Meeting Date *</Label>
            <Input
              id="meeting_date"
              type="date"
              value={workflowData.meeting_date || ''}
              onChange={(e) =>
                setWorkflowData({ ...workflowData, meeting_date: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="meeting_time">Meeting Time *</Label>
            <Input
              id="meeting_time"
              type="time"
              value={workflowData.meeting_time || ''}
              onChange={(e) =>
                setWorkflowData({ ...workflowData, meeting_time: e.target.value })
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meeting_duration">Meeting Duration (Estimated) *</Label>
          <Select
            value={workflowData.meeting_duration}
            onValueChange={(value) =>
              setWorkflowData({ ...workflowData, meeting_duration: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30 minutes">30 minutes</SelectItem>
              <SelectItem value="1 hour">1 hour</SelectItem>
              <SelectItem value="1.5 hours">1.5 hours</SelectItem>
              <SelectItem value="2 hours">2 hours</SelectItem>
              <SelectItem value="2.5 hours">2.5 hours</SelectItem>
              <SelectItem value="3 hours">3 hours</SelectItem>
              <SelectItem value="Half day">Half day</SelectItem>
              <SelectItem value="Full day">Full day</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meeting_location">Meeting Location *</Label>
          <Input
            id="meeting_location"
            value={workflowData.meeting_location || ''}
            onChange={(e) =>
              setWorkflowData({ ...workflowData, meeting_location: e.target.value })
            }
            placeholder="e.g., 1234 Main St, Conference Room B or Zoom - link to be provided"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="meeting_format">Meeting Format *</Label>
          <Select
            value={workflowData.meeting_format}
            onValueChange={(value) =>
              setWorkflowData({ ...workflowData, meeting_format: value })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="In-Person">In-Person</SelectItem>
              <SelectItem value="Virtual (Zoom/Video)">Virtual (Zoom/Video)</SelectItem>
              <SelectItem value="Hybrid">Hybrid</SelectItem>
              <SelectItem value="Conference Call">Conference Call</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meeting_conductor">Meeting Conductor *</Label>
          <Select
            value={workflowData.meeting_conductor_id}
            onValueChange={(value) => {
              const conductor = meetingConductors.find(c => c.id === value);
              setWorkflowData({
                ...workflowData,
                meeting_conductor_id: value,
                meeting_conductor_name: conductor?.name || '',
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select meeting conductor" />
            </SelectTrigger>
            <SelectContent>
              {meetingConductors.map(conductor => (
                <SelectItem key={conductor.id} value={conductor.id}>
                  {conductor.name} - {conductor.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Who will lead/chair this meeting?
          </p>
        </div>
      </div>

      {/* Attendees Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Attendees</h3>
        
        <div className="space-y-2">
          <Label>Required Attendees *</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Select all members who must attend
          </p>
          <div className="space-y-2 border rounded-md p-4">
            {allAttendees.map(attendee => (
              <div key={attendee.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`required-${attendee.id}`}
                  checked={workflowData.required_attendees?.some((a: any) => a.id === attendee.id)}
                  onCheckedChange={() => toggleRequiredAttendee(attendee)}
                />
                <label
                  htmlFor={`required-${attendee.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {attendee.full_name} - {attendee.role}
                </label>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-2">
            Tip: For Board Meetings, all board members should be required
          </p>
        </div>

        <div className="space-y-2">
          <Label>Optional Attendees</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Select members who may attend but aren't required
          </p>
          <div className="space-y-2 border rounded-md p-4">
            {allAttendees.map(attendee => (
              <div key={attendee.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`optional-${attendee.id}`}
                  checked={workflowData.optional_attendees?.some((a: any) => a.id === attendee.id)}
                  onCheckedChange={() => toggleOptionalAttendee(attendee)}
                />
                <label
                  htmlFor={`optional-${attendee.id}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {attendee.full_name} - {attendee.role}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>External Guests (Optional)</Label>
          <p className="text-sm text-muted-foreground mb-2">
            Add any non-members who will attend
          </p>
          {externalGuests.map((guest, index) => (
            <div key={index} className="border rounded-md p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Guest {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeExternalGuest(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input
                  placeholder="Guest Name"
                  value={guest.guest_name}
                  onChange={(e) =>
                    updateExternalGuest(index, 'guest_name', e.target.value)
                  }
                />
                <Input
                  placeholder="Role/Title"
                  value={guest.guest_role}
                  onChange={(e) =>
                    updateExternalGuest(index, 'guest_role', e.target.value)
                  }
                />
                <Input
                  placeholder="Organization"
                  value={guest.guest_organization}
                  onChange={(e) =>
                    updateExternalGuest(index, 'guest_organization', e.target.value)
                  }
                />
                <Input
                  placeholder="Email (optional)"
                  type="email"
                  value={guest.guest_email || ''}
                  onChange={(e) =>
                    updateExternalGuest(index, 'guest_email', e.target.value)
                  }
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={addExternalGuest}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add External Guest
          </Button>
        </div>
      </div>

      {/* Meeting Materials & Notifications Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Meeting Materials & Notifications</h3>
        
        <div className="space-y-2">
          <Label>Recording Officer Assignment *</Label>
          <Select
            value={workflowData.recording_secretary_id}
            onValueChange={(value) => {
              const officer = recordingOfficers.find(o => o.id === value);
              setWorkflowData({
                ...workflowData,
                recording_secretary_id: value,
                recording_secretary_name: officer?.name || '',
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select recording officer" />
            </SelectTrigger>
            <SelectContent>
              {recordingOfficers.map(officer => (
                <SelectItem key={officer.id} value={officer.id}>
                  {officer.name} - {officer.role}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            Who will take minutes during the meeting?
          </p>
        </div>

        <div className="space-y-2">
          <Label>Meeting Notes (Optional)</Label>
          <Textarea
            placeholder="Any additional information or special instructions..."
            value={workflowData.meeting_notes || ''}
            onChange={(e) =>
              setWorkflowData({ ...workflowData, meeting_notes: e.target.value })
            }
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};